import { createContext, PropsWithChildren, useState } from 'react'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { AppRoutes } from '../utils/constants'
import { useNavigate } from 'react-router-dom'
import { useUsermaven } from '@usermaven/react'
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query'
import { useNotifications } from './notificationContext'
import { DAIRequest } from '../utils/DAIRequest'
import { useModal } from './modalContext'
import DescriptionModal from 'src/pages/Common/Description'

export type User = {
  id: string
  firstName: string
  lastName: string
  profilePicture: string
  email: string
  role: string
  organizationId: string
  timezone: string
}

export type LoginType = {
  email: string
  password: string
  rememberMe: boolean
}

type AuthContextType = {
  isAuthenticated: boolean
  isLoading: boolean
  isEmailIntegrationComplete: boolean
  user?: User
  logout: () => void
  loginMutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    LoginType,
    unknown
  >
  notifyUserToSetupIntegration: (notify?: boolean) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isEmailIntegrationComplete, setIsEmailIntegrationComplete] =
    useState(false)
  const { addNotification, removeAllNotifications } = useNotifications()
  const { id } = useUsermaven()
  const { openModal, closeModal } = useModal()

  // Define the function that fetches user data
  const {
    isLoading,
    data: user,
    refetch,
  } = useQuery<User, Error>({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await DAIRequest('get', '/api/v1/users/me')
      setIsAuthenticated(true)
      setIsEmailIntegrationComplete(response.data.isEmailIntegrationComplete)
      return response.data
    },
    retry: false,
  })

  const logout = async () => {
    localStorage.removeItem('token')
    setIsAuthenticated(false)
    removeAllNotifications()
    addNotification('success', 'Logged out successfully')
    navigate(AppRoutes.login)
    await refetch()
  }

  const loginMutation = useMutation({
    mutationFn: async (data: LoginType) => {
      return await DAIRequest('post', '/api/v1/authentication/login', {
        body: data,
      })
    },
    onSuccess: async res => {
      localStorage.setItem('token', res.data.token)
      axios.defaults.headers.common['Authorization'] =
        `Bearer ${res.data.token}`
      addNotification('success', 'Login successful')
      setIsAuthenticated(true)
      navigate(AppRoutes.home)
      await id({ id: res.data.organizationId, email: res.data.email })
      setIsEmailIntegrationComplete(res.data.isEmailIntegrationComplete)
      notifyUserToSetupIntegration(!res.data.isEmailIntegrationComplete)
      await refetch()
    },
    onError: (err: AxiosError) => {
      setIsAuthenticated(false)
      if (err.response?.status === 401) {
        return addNotification('error', 'Incorrect Password')
      }
      addNotification('error', 'Login failed')
    },
  })

  var notifyUserToSetupIntegration = (notify?: boolean) => {
    if (notify ?? !isEmailIntegrationComplete) {
      openModal(
        <DescriptionModal
          title="Email Integration Needed"
          description="You need to set up your email integration inside of Settings"
          showButton={true}
          buttonText="Go"
          onButtonClick={() => {
            navigate(AppRoutes.integrationSettings)
            closeModal()
          }}
        />
      )
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        logout,
        loginMutation,
        isEmailIntegrationComplete,
        notifyUserToSetupIntegration,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
